/** Describes the format of a string as a flat file of rows and columns. */
export interface FlatFileFormat {
  /** column delimiter */
  columnDelimiter: string;
  /** row delimiter */
  rowDelimiter: string;
  /** surrounds each cell */
  textQaulifier: string;
  /** when the text qualifier is found, how it is escaped */
  escapedTextQualifier: string;
}

/** constant with settings used for csv files */
export const CSV_FORMAT: Readonly<FlatFileFormat> = {
  columnDelimiter: ',',
  rowDelimiter: '\n',
  textQaulifier: '"',
  escapedTextQualifier: '""'
};

/** constant with settings used for tab delimited files */
export const TAB_DELIMITED_FORMAT: Readonly<FlatFileFormat> = {
  columnDelimiter: '\t',
  rowDelimiter: '\n',
  textQaulifier: '"',
  escapedTextQualifier: '""'
};

export function covertArrayToFlatFile(data: unknown[][], fileFormat: FlatFileFormat): string {
  const textQualiferRe = createEscapeRegExp(fileFormat.textQaulifier);

  return data
    .map(row => row.map(x => createCellContent(x)).join(fileFormat.columnDelimiter)) // create row string
    .join(fileFormat.rowDelimiter);

  function createCellContent(cellData: unknown) {
    if (cellData == null) return '';
    const text = '' + cellData; // quick conversion to string;
    if (text.indexOf(fileFormat.columnDelimiter) === -1
      && text.indexOf(fileFormat.textQaulifier) === -1
      && text.indexOf(fileFormat.rowDelimiter) === -1) {
      return text;
    }

    return fileFormat.textQaulifier
      + (text).replace(textQualiferRe, fileFormat.escapedTextQualifier)
      + fileFormat.textQaulifier;
  }

  // Creates regex that will find escape characters in a string.  Will escape any escape characters so \t will be /\\t/
  function createEscapeRegExp(str: string): RegExp {
    return new RegExp(str.replace(/([.*+?^=!:${}()|\[\]\/\\])/g, '\\$1'), 'g');
  }
}
/** Converts a string that is like a csv or tab delimited file into a 2d string array. */
export function convertFlatFileTextToArray(text: string, fileFormat: FlatFileFormat): string[][] {
  interface ParserContext {
    /** Current cell text. */
    cellText: string;
    /** Built up output. */
    output: string[][];
    parserMode: 'normal' | 'qualifiedString';
    /** Current row data.  */
    row: string[];
  }

  const context: ParserContext = {
    cellText: '',
    output: [],
    parserMode: 'normal',
    row: []
  };

  const tokensRe = tokensRegexFromFileFormat(fileFormat);
  let match: RegExpExecArray | null;
  let groupIndex: number;

  // tslint:disable-next-line: no-conditional-assignment
  while ((match = tokensRe.exec(text)) !== null) {
    groupIndex = getGroupIndex(match);
    switch (context.parserMode) {
      case 'normal':
        parseNormal(match, groupIndex);
        break;
      case 'qualifiedString':
        parseQualifiedString(match, groupIndex);
        break;
    }
  }
  flushText(false);
  flushRow(false);

  return context.output;


  /**
   * copies text to current row, and reset text to an empty string
   * @param forceFlush if true copies even if text is empty, otherwise checks to make sure text isn't before copying
   */
  function flushRow(forceFlush: boolean): void {
    if (forceFlush || context.row.length > 1 || context.row.length === 1 && context.row[0] !== '') {
      context.output.push(context.row);
      context.row = [];
    }
  }

  /**
   * copies text to current row, and reset text to an empty string
   * @param forceFlush if true copies even if text is empty, otherwise checks to make sure text isn't before copying
   */
    function flushText(forceFlush: boolean): void {
    if (forceFlush || context.cellText !== '') {
      context.row.push(context.cellText);
      context.cellText = '';
    }
  }

  /**
   * gets the group index from the matched token in match.
   */
  function getGroupIndex(match: RegExpExecArray): number {
    for (let i = 1; i < match.length; i++) {
      if (match[i] !== undefined) {
        return i;
      }
    }
    return -1;
  }

  /** Parses in normal mode. */
  function parseNormal(match: RegExpExecArray, groupIndex: number): void {
    switch (groupIndex) {
      case 1: // text qualifier
        context.parserMode = 'qualifiedString';
        break;
      case 3: // column delimeter;
        flushText(true);
        break;
      case 4: // row delimeter
        flushText(false);
        flushRow(true);
        break;
      default: // any other string
        context.cellText += match[0];
        break;
    }
  }

  /** Parses in qualified string mode. */
  function parseQualifiedString(match: RegExpExecArray, groupIndex: number): void {
    switch (groupIndex) {
      case 1: // text qualifier
        context.parserMode = 'normal';
        break;
      case 2: // escaped text qualifier
        context.cellText += fileFormat.textQaulifier;
        break;
      default: // any other string
        context.cellText += match[0];
        break;
    }
  }

  /**
   * Creates a regex from FlatFileFormat in the order of textQualifer, escapedTextQualifer, columnDelim, rowDelim, anything else.
   */
  function tokensRegexFromFileFormat(ff: FlatFileFormat) {
    function createTokenPart(text: string) {
      return '(' + text.replace(/[\\().[]{}]/g, '\\$') + ')|';
    }
    return new RegExp(createTokenPart(ff.textQaulifier)
      + createTokenPart(ff.escapedTextQualifier)
      + createTokenPart(ff.columnDelimiter)
      + createTokenPart(ff.rowDelimiter)
      + '.+?', 'g');
  }
}
